import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Box, Button, Collapse, Flex, HStack, IconButton, Spinner, Text, VStack } from "@chakra-ui/react";
import { Files, Plus, WarningDiamond, X } from "@phosphor-icons/react";
import { useQueryClient } from "react-query";
import fileinfoService, { apiUpload } from "../../../../api/common/fileinfo.service";
import FileDownloadButton from "../../../../components/FileDownloadButton";
import FileDragDrop from "../../../../components/FileDragDrop";
import FilePill from "./FilePill";
import LibraryPicker from "./LibraryPicker";
import { ColHeader } from "./MethodologyCard";

const MAX_FILE_SIZE_MB = Number(process.env.REACT_APP_MAX_FILE_SIZE_MB) || 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_FILE_SIZE_READABLE = `${MAX_FILE_SIZE_MB} MB`;

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  const idx = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const v = bytes / Math.pow(1024, idx);
  return `${v.toFixed(idx === 0 ? 0 : 1)} ${units[idx]}`;
};

// Sources card body. Order from top to bottom: drop zone → library link →
// "Attached · n" eyebrow → attached list. In read-only mode the upload zone,
// library link and remove buttons disappear; only the attached list (or an
// empty placeholder) is shown.
function SourcesCard({ fileInfoList, onFileInfoListChange, folder, readOnly = false, isOpen, onToggle }) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [fileInfoLoading, setFileInfoLoading] = useState([]);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [librarySearch, setLibrarySearch] = useState("");
  const [libraryFiles, setLibraryFiles] = useState(null);

  const fetchLibrary = async () => {
    setLibraryOpen(true);
    setLibraryFiles(null);
    const page = await fileinfoService.list({ size: 1000 });
    const files = page?.content || [];
    const filtered = files.filter((f) => !f.archived && !fileInfoList.some((fi) => fi.id === f.id));
    setLibraryFiles(filtered);
  };

  const handleSelectLibraryFile = (fileInfo) => {
    onFileInfoListChange([...fileInfoList, fileInfo]);
    setLibraryFiles((prev) => prev?.filter((f) => f.id !== fileInfo.id));
  };

  const handleRemoveFile = (fileInfo) => {
    onFileInfoListChange(fileInfoList.filter((f) => f.id !== fileInfo.id));
  };

  const closeLibrary = () => {
    setLibraryOpen(false);
    setLibrarySearch("");
    setLibraryFiles(null);
  };

  // Whenever the surrounding accordion collapses, drop the library picker so
  // the next open shows the upload zone — not whatever sub-mode the user
  // happened to leave behind.
  useEffect(() => {
    if (!isOpen) closeLibrary();
  }, [isOpen]);

  // Replicates the existing SourcesPanel upload flow — keeps the same query
  // invalidation key so any other mounted file lists refresh.
  const handleFileUpload = (files) => {
    if (!files || !files[0]) return;
    const fileArray = Array.from(files);
    const validFiles = [];

    for (const file of fileArray) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setFileInfoLoading((prev) => [...prev, {
          id: `error_${file.name}_${Date.now()}`,
          filename: file.name,
          size: file.size,
          loading: false,
          error: true,
          errorMessage: `Too large (max ${MAX_FILE_SIZE_READABLE})`,
        }]);
      } else {
        validFiles.push(file);
      }
    }

    for (const newFile of validFiles) {
      setFileInfoLoading((prev) => [...prev, {
        id: newFile.name,
        filename: newFile.name,
        size: newFile.size,
        loading: true,
      }]);
    }

    const list = [...fileInfoList];
    (async () => {
      for (const newFile of validFiles) {
        const formData = new FormData();
        formData.append("file", newFile);
        if (folder) formData.append("folders", [folder]);
        try {
          const newFileInfo = await apiUpload({ data: formData });
          setFileInfoLoading((prev) =>
            prev.filter((f) => !(f.filename === newFile.name && f.size === newFile.size))
          );
          queryClient.invalidateQueries("fileinfoService.list");
          list.push(newFileInfo);
          onFileInfoListChange([...list]);
        } catch {
          setFileInfoLoading((prev) =>
            prev.map((f) =>
              f.filename === newFile.name && f.size === newFile.size
                ? { ...f, loading: false, error: true, errorMessage: "Upload failed" }
                : f
            )
          );
        }
      }
    })();
  };

  const showUploadAffordances = !readOnly && !libraryOpen;
  const attachedCount = fileInfoList.length;

  return (
    <Box
      bg="pureWhite"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="14px"
      p="22px 24px"
      h="100%"
    >
      <ColHeader
        icon={<Files size={14} color="var(--chakra-colors-limeShade-600)" />}
        eyebrow="Sources"
        title="Is additional information required?"
        sub="Attach any documentary evidence needed to support this data or methodology."
        isOpen={isOpen}
        onToggle={onToggle}
        badgeCount={fileInfoList.length}
      />

      <Collapse in={isOpen} animateOpacity>
      {(attachedCount > 0 || fileInfoLoading.length > 0 || readOnly) && (
        <>
          <Text
            fontSize="10px"
            fontWeight="700"
            color="gray.400"
            letterSpacing="0.06em"
            textTransform="uppercase"
            mb="6px"
          >
            Attached · {attachedCount}
          </Text>

          <VStack spacing="6px" align="stretch" mb={3}>
            {fileInfoLoading.map((file) => (
              <HStack
                key={file.id}
                spacing={3}
                border="1px solid"
                borderColor="gray.100"
                borderRadius="8px"
                px="10px"
                py="9px"
              >
                {file.error ? <WarningDiamond size={16} color="#E53E3E" /> : <Spinner size="xs" />}
                <Text fontSize="12px" color={file.error ? "red.500" : "gray.600"} flex={1} noOfLines={1}>
                  {file.filename}{file.error && ` — ${file.errorMessage}`}
                </Text>
              </HStack>
            ))}

            {attachedCount === 0 && fileInfoLoading.length === 0 && readOnly && (
              <Box bg="gray.50" borderRadius="8px" p="14px">
                <Text fontSize="12.5px" color="gray.400" fontStyle="italic">
                  No sources attached.
                </Text>
              </Box>
            )}

            {fileInfoList.map((fileInfo) => {
              const sizeLabel = formatBytes(fileInfo.size ?? fileInfo.fileSize);
              const uploadedBy = fileInfo.uploadedBy || fileInfo.uploadedByName || fileInfo.createdBy;
              const meta = [sizeLabel, uploadedBy].filter(Boolean).join(" · ");
              return (
                <HStack
                  key={fileInfo.id}
                  spacing={3}
                  border="1px solid"
                  borderColor="gray.100"
                  borderRadius="8px"
                  px="10px"
                  py="9px"
                >
                  <FilePill fileInfo={fileInfo} size={9} />
                  <Box flex={1} minW={0}>
                    <FileDownloadButton
                      fileInfo={fileInfo}
                      as={Text}
                      fontSize="12px"
                      fontWeight="600"
                      color="darkTeal"
                      noOfLines={1}
                    >
                      {fileInfo.title || fileInfo.filename || fileInfo.id}
                    </FileDownloadButton>
                    {meta && (
                      <Text fontSize="10px" color="gray.400" mt="2px">
                        {meta}
                      </Text>
                    )}
                  </Box>
                  {!readOnly && (
                    <IconButton
                      aria-label="Remove file"
                      icon={<X size={12} />}
                      onClick={() => handleRemoveFile(fileInfo)}
                      variant="ghost"
                      size="xs"
                      color="gray.400"
                      _hover={{ color: "red.500" }}
                    />
                  )}
                </HStack>
              );
            })}
          </VStack>
        </>
      )}

      {showUploadAffordances && (
        <FileDragDrop onFiles={handleFileUpload} borderRadius="12px">
          <VStack
            border="1.5px dashed"
            borderColor="rgba(133,194,42,0.4)"
            bg="lightLime"
            opacity={0.95}
            borderRadius="12px"
            p="24px 16px"
            spacing={1}
            cursor="pointer"
            mb={2}
            _hover={{ borderColor: "lime", opacity: 1 }}
            onClick={() => fileInputRef.current?.click()}
          >
            <Plus size={18} color="var(--chakra-colors-limeShade-600)" />
            <Text fontSize="13px" fontWeight="700" color="darkTeal" mt={1}>
              Drop or click to upload
            </Text>
            <Text fontSize="11px" color="whiteShade.600">
              PDF, DOCX, XLSX, CSV · {MAX_FILE_SIZE_READABLE}
            </Text>
          </VStack>
        </FileDragDrop>
      )}
      <input
        type="file"
        multiple
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={(e) => { handleFileUpload(e.target.files); e.target.value = null; }}
        accept=".pdf,.docx,.xlsx,.xls,.doc,.csv,.pptx,.ppt,.txt,.png,.jpg,.jpeg"
      />

      {showUploadAffordances && (
        <Button
          variant="ghost"
          size="sm"
          color="limeShade.600"
          fontSize="12px"
          fontWeight="600"
          pl={0}
          pr={0}
          mb={3}
          onClick={fetchLibrary}
          _hover={{ bg: "transparent", color: "limeShade.700" }}
        >
          + Use from library
        </Button>
      )}

      {!readOnly && libraryOpen && (
        <LibraryPicker
          libraryFiles={libraryFiles}
          isLoading={!libraryFiles}
          search={librarySearch}
          onSearchChange={setLibrarySearch}
          onPick={handleSelectLibraryFile}
          onClose={closeLibrary}
        />
      )}
      </Collapse>
    </Box>
  );
}

export default SourcesCard;
